import React, { useState, useRef, useEffect } from 'react';
import {
  Mic, Globe, User, Zap, Send, Star, Mail, MessageSquare, Volume2, CheckCircle, Loader2, Download
} from 'lucide-react';
import { synthesizeSpeech, submitFeedback } from '../services/api';
import toast from 'react-hot-toast';

/* ── Constants ─────────────────────────────────────────────────── */
const LANGUAGES = [
  { code: 'en-US', label: 'English' },
  { code: 'nl-NL', label: 'Dutch' },
  { code: 'fr-FR', label: 'French' },
  { code: 'it-IT', label: 'Italian' },
  { code: 'es-ES', label: 'Spanish' },
  { code: 'de-DE', label: 'German' },
  { code: 'sv-SE', label: 'Swedish' },
  { code: 'pl-PL', label: 'Polish' },
];

const MAX_CHARS = 5000;

/* ── Custom Player Component ────────────────────────────────────────── */
const FALLBACK_WAVE = [
  25, 40, 60, 90, 75, 45, 55, 80, 100, 85,
  65, 45, 30, 20, 35, 60, 80, 75, 95, 85,
  70, 55, 40, 30, 45, 65, 85, 90, 75, 60,
  45, 30, 25, 35, 55, 75, 65, 45, 35, 25,
  20, 30, 45, 40, 30
];

const CustomAudioPlayer = ({ src, lang }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [waveform, setWaveform] = useState(FALLBACK_WAVE);

  // Dynamic Waveform Extraction
  useEffect(() => {
    if (!src) return;
    const extractPeaks = async () => {
      try {
        const response = await fetch(src);
        const arrayBuffer = await response.arrayBuffer();
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        
        const channelData = audioBuffer.getChannelData(0);
        const bins = 45;
        const binSize = Math.floor(channelData.length / bins);
        
        const newWave = [];
        let maxVal = 0.001; // Avoid divide by zero
        
        for (let i = 0; i < bins; i++) {
          let sum = 0;
          for (let j = 0; j < binSize; j++) {
            const val = channelData[i * binSize + j];
            sum += val * val;
          }
          const rms = Math.sqrt(sum / binSize);
          newWave.push(rms);
          if (rms > maxVal) maxVal = rms;
        }
        
        const normalized = newWave.map(v => Math.max(15, (v / maxVal) * 100));
        setWaveform(normalized);
      } catch (err) {
        console.warn('Could not extract audio peaks, using fallback.', err);
        setWaveform(FALLBACK_WAVE);
      }
    };
    extractPeaks();
  }, [src]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateProgress = () => setProgress(audio.currentTime / (audio.duration || 1));
    const handleEnded = () => { setIsPlaying(false); setProgress(0); };
    
    audio.addEventListener('timeupdate', updateProgress);
    audio.addEventListener('ended', handleEnded);
    return () => {
      audio.removeEventListener('timeupdate', updateProgress);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [src]);

  useEffect(() => {
    setIsPlaying(false);
    setProgress(0);
    if (audioRef.current && src) {
      audioRef.current.load();
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [src]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    isPlaying ? audio.pause() : audio.play();
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const audio = audioRef.current;
    if (audio && audio.duration) {
      audio.currentTime = pct * audio.duration;
      setProgress(pct);
    }
  };

  const handleDownload = () => {
    if (!src) return;
    const a = document.createElement('a');
    a.href = src;
    a.download = `speech-${lang || 'audio'}-${Date.now()}.wav`;
    a.click();
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
      <audio ref={audioRef} src={src} preload="metadata" />
      
      <button 
        type="button"
        onClick={togglePlay}
        style={{
          width: 50, height: 50, borderRadius: '50%',
          border: '2px solid #ef145a', background: 'transparent',
          color: '#ef145a', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', flexShrink: 0, padding: 0
        }}
        title={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying ? (
          <div style={{ width: 14, height: 14, background: '#ef145a', borderRadius: 2 }} />
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: 4 }}>
            <path d="M6 4l15 8-15 8V4z" />
          </svg>
        )}
      </button>

      <div 
        onClick={handleSeek}
        style={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1, height: 44, cursor: 'pointer' }}
      >
        {waveform.map((val, i) => {
          const isPlayed = (i / waveform.length) <= progress;
          return (
            <div 
              key={i} 
              style={{
                flex: 1, height: `${val}%`, minHeight: 4,
                backgroundColor: isPlayed ? '#ef145a' : '#52525b',
                borderRadius: 10, transition: 'background-color 0.1s linear'
              }}
            />
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleDownload}
        title="Download Audio"
        style={{
          width: 40, height: 40, borderRadius: '50%',
          border: 'none', background: 'rgba(255,255,255,0.06)',
          color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
      >
        <Download size={18} />
      </button>
    </div>
  );
};

/* ── Main component ────────────────────────────────────────────── */
const UnifiedPage = () => {
  // TTS Fields with LocalStorage caching
  const [email, setEmail] = useState(() => localStorage.getItem('piper_email') || '');
  const [lang, setLang] = useState(() => localStorage.getItem('piper_lang') || 'en-US');
  const [gender, setGender] = useState(() => localStorage.getItem('piper_gender') || 'female');
  const [text, setText] = useState('');
  
  // Persist selections to localStorage
  useEffect(() => { localStorage.setItem('piper_email', email); }, [email]);
  useEffect(() => { localStorage.setItem('piper_lang', lang); }, [lang]);
  useEffect(() => { localStorage.setItem('piper_gender', gender); }, [gender]);

  // Audio state
  const [audioResult, setAudioResult] = useState(null); // { blobUrl, s3Url }
  const [isPlaying, setIsPlaying] = useState(false);
  const [ttsLoading, setTtsLoading] = useState(false);
  const audioRef = useRef(null);

  // Feedback fields
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [remark, setRemark] = useState('');
  const [fbLoading, setFbLoading] = useState(false);
  const [fbDone, setFbDone] = useState(false);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => { if (audioResult?.blobUrl) URL.revokeObjectURL(audioResult.blobUrl); };
  }, [audioResult]);

  /* ── Handlers ──────────────────────────────────────────────────── */
  const handleGenerate = async () => {
    if (!text.trim()) { toast.error('Please enter text to speak'); return; }
    
    setTtsLoading(true);
    setAudioResult(null);
    setFbDone(false);

    try {
      const result = await synthesizeSpeech({ text, lang, gender });
      setAudioResult(result);
      toast.success('Audio generated successfully!');
    } catch (err) {
      const msg = err.response?.data?.error || 'TTS synthesis failed';
      toast.error(msg);
    } finally {
      setTtsLoading(false);
    }
  };

  const handleSubmitAll = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Email is required'); return; }
    if (!audioResult) { toast.error('Please generate and listen to the audio first'); return; }
    if (rating === 0) { toast.error('Please select a rating'); return; }

    setFbLoading(true);
    try {
      await submitFeedback({
        email,
        language: lang,
        gender,
        text,
        remark,
        rating,
        audioUrl: audioResult?.s3Url || '',
      });
      setFbDone(true);
      toast.success('Feedback submitted! Thank you.');
    } catch (err) {
      const msgs = err.response?.data?.errors || [err.response?.data?.error || 'Submission failed'];
      msgs.forEach((m) => toast.error(m));
    } finally {
      setFbLoading(false);
    }
  };

  const handleReset = () => {
    setText('');
    setAudioResult(null);
    setFbDone(false);
    setRating(0);
    setRemark('');
  };

  const charPct = (text.length / MAX_CHARS) * 100;
  const charClass = charPct > 90 ? 'danger' : charPct > 70 ? 'warn' : '';

  /* ── Render ────────────────────────────────────────────────────── */
  return (
    <div className="fade-in" style={{ maxWidth: 640, margin: '0 auto', paddingBottom: 60 }}>

      <div className="card">
        <form onSubmit={handleSubmitAll}>
          
          {/* Email */}
          <div className="form-group">
            <label className="form-label">
              <Mail size={12} style={{ display: 'inline', marginRight: 4 }} /> Email Address
            </label>
            <input
              type="email"
              className="form-control"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={ttsLoading || fbLoading}
              required
            />
          </div>

          <div className="select-row">
            {/* Language */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">
                <Globe size={12} style={{ display: 'inline', marginRight: 4 }} /> Language Used
              </label>
              <select
                className="form-control"
                value={lang}
                onChange={(e) => { setLang(e.target.value); setAudioResult(null); }}
                disabled={ttsLoading || fbLoading}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            </div>

            {/* Gender */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">
                <User size={12} style={{ display: 'inline', marginRight: 4 }} /> Voice Gender
              </label>
              <div className="gender-toggle">
                {['female', 'male'].map((g) => (
                  <button
                    key={g}
                    type="button"
                    className={`gender-btn ${gender === g ? 'active' : ''}`}
                    onClick={() => { setGender(g); setAudioResult(null); }}
                    disabled={ttsLoading || fbLoading}
                  >
                    {g === 'female' ? '👩 Female' : '👨 Male'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="form-group" style={{ marginTop: 18 }}>
            <label className="form-label">Text to Translate/Speak</label>
            <textarea
              className="form-control"
              value={text}
              onChange={(e) => { setText(e.target.value); setAudioResult(null); }}
              placeholder="Type the text you want the AI to speak..."
              style={{ minHeight: 120 }}
              maxLength={MAX_CHARS}
              disabled={ttsLoading || fbLoading}
              required
            />
            <div className={`char-count ${charClass}`}>
              {text.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
            </div>
          </div>

          {/* Generate Button */}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={ttsLoading || !text.trim() || fbLoading}
            style={{ marginBottom: 12 }}
          >
            {ttsLoading ? (
              <><Loader2 size={16} className="spin" /> Generating Audio...</>
            ) : (
              <><Mic size={16} /> Generate Audio</>
            )}
          </button>
          
          {ttsLoading && (
            <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              (Audio model downloads automatically on the first request for a language. This may take a minute.)
            </div>
          )}

          {/* Audio Player (conditionally rendered) */}
          {audioResult && (
            <div style={{
              marginTop: 24, padding: "20px 0",
              borderTop: '1px solid var(--border-subtle)'
            }} className="fade-in">
              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>
                Generated Speech
              </div>
              
              <CustomAudioPlayer src={audioResult.blobUrl} lang={lang} />

              <hr style={{ border: 0, borderTop: '1px solid var(--border-subtle)', margin: '24px 0' }} />

              {/* Feedback Section */}
              <div className="form-group" style={{ marginBottom: 20 }}>
                <label className="form-label">
                  <Star size={12} style={{ display: 'inline', marginRight: 4 }} /> Voice Quality Rating
                </label>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="star-btn"
                      style={{ padding: 4 }}
                      onMouseEnter={() => setHoveredStar(s)}
                      onMouseLeave={() => setHoveredStar(0)}
                      onClick={() => setRating(s)}
                      disabled={fbLoading}
                    >
                      <Star
                        size={32}
                        fill={s <= (hoveredStar || rating) ? '#fbbf24' : 'none'}
                        color={s <= (hoveredStar || rating) ? '#fbbf24' : 'var(--text-muted)'}
                        style={{ transition: 'all 0.15s ease' }}
                      />
                    </button>
                  ))}
                  <div style={{ marginLeft: 10, alignSelf: 'center', fontSize: '0.85rem', color: '#fbbf24', fontWeight: 600 }}>
                    {['', '1 - Poor', '2 - Fair', '3 - Good', '4 - Very Good', '5 - Excellent'][hoveredStar || rating]}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  <MessageSquare size={12} style={{ display: 'inline', marginRight: 4 }} /> Additional Remarks (Optional)
                </label>
                <textarea
                  className="form-control"
                  placeholder="Tell us what you liked or disliked about the speech generation..."
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  disabled={fbLoading}
                  style={{ minHeight: 80 }}
                />
              </div>

              {!fbDone ? (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={fbLoading || rating === 0}
                  style={{ background: 'var(--accent-primary)' }}
                >
                  {fbLoading ? (
                    <><Loader2 size={16} className="spin" /> Submitting...</>
                  ) : (
                    <><Send size={16} /> Submit Rating & Remarks</>
                  )}
                </button>
              ) : (
                <div style={{ background: 'rgba(74,222,128,0.1)', border: '1px solid rgba(74,222,128,0.3)', padding: 16, borderRadius: 10, textAlign: 'center' }}>
                  <CheckCircle size={24} color="var(--accent-green)" style={{ margin: '0 auto 8px' }} />
                  <div style={{ color: '#fff', fontWeight: 600, marginBottom: 4 }}>Feedback Submitted!</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 12 }}>Your feedback helps us improve the quality.</div>
                  <button type="button" className="btn btn-secondary" onClick={handleReset} style={{ padding: '8px 16px', fontSize: '0.8rem' }}>
                    Generate Another
                  </button>
                </div>
              )}
            </div>
          )}

        </form>
      </div>

      <style>{`.spin{animation:spin .7s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default UnifiedPage;
